/* eslint-disable max-classes-per-file */
import { Alert } from '@/shared/components/rebranded/Alert/Alert';
import Button from '@/shared/components/rebranded/Button/Button';
import Popover from '@storeblocks/popover';
import { CSSProperties, Component, MouseEvent, ReactNode } from 'react';
import isDeepEqual from 'react-fast-compare';
import { Legend } from '../../shared/components/Form/Form';
import Paper from '../../shared/components/Paper/Paper';
import {
  ArraySchema,
  Errors,
  FieldBlurEvent,
  FieldChangeEvent,
  FieldProps,
  ObjectSchema,
  Schema,
  SchemaValueType,
  Touched,
  isObjectSchema
} from '../types';
import { evaluateSubschema } from '../validation/evaluateSchema';
import {
  getSchemaValues,
  guid,
  joinErrors,
  orderProperties
} from '../validation/utils';
import Field from './Field';
import { OptionalHint } from './OptionalHint/OptionalHint';

type Props = Omit<
  FieldProps<ArraySchema<ObjectSchema>>,
  'value' | 'touched'
> & {
  value: Array<{ _id: number } & SchemaValueType<ObjectSchema>>;
  touched: Array<{ _id: number } & Touched<ObjectSchema>>;
  renderItem?: (item: Omit<ArrayItemProps, 'render'>) => ReactNode;
  renderAddButton?: (props: AddButtonProps) => ReactNode;
  addItemButtonText?: string | null;
  removeItemButtonText?: string | null;
  itemCountText?: string | null;
  style?: CSSProperties;
  className?: string;
  noOptimalisation?: boolean;
  asTable?: boolean;
  showOptionalHint?: boolean;
  getText: (key: string) => string;
};

export type ArrayItemProps = {
  fields: { [key: string]: FieldProps<Schema, any> };
  onRemove: () => void;
  onReset: () => void;
  removeItemButtonText?: string | null;
  render: (props: Omit<ArrayItemProps, 'render'>) => ReactNode;
  index: number;
  id: number;
  noOptimalisation?: boolean;
  isLastItem: boolean;
};

export type AddButtonProps = {
  value: string;
  onClick: (e: MouseEvent) => void;
};

class HeavilyOptimizedArrayItemField extends Component<ArrayItemProps> {
  shouldComponentUpdate(prevProps: ArrayItemProps) {
    if (prevProps.noOptimalisation) {
      return true;
    }
    const fields = Object.values(this.props.fields);
    const prevFields = Object.values(prevProps.fields);

    if (fields.length !== prevFields.length) {
      return true;
    }

    for (let i = 0; i < fields.length; i++) {
      if (fields[i].value !== prevFields[i].value) {
        return true;
      }

      if (!isDeepEqual(fields[i].errors, prevFields[i].errors)) {
        return true;
      }

      if (!isDeepEqual(fields[i].touched, prevFields[i].touched)) {
        return true;
      }

      if (fields[i].forceShowError !== prevFields[i].forceShowError) {
        return true;
      }
    }

    return false;
  }

  render() {
    const { render, ...props } = this.props;
    return render(props);
  }
}

const findByItemId = <T extends { _id: number }, U>(
  collection: Array<T>,
  id: number,
  defaultValue: U
): any => (collection || []).find(item => item._id === id) || defaultValue;

class ArrayField extends Component<Props> {
  static defaultProps = {
    addItemButtonText: 'Add',
    removeItemButtonText: 'Remove',
    value: [],
    touched: [],
    asTable: false,
    renderItem: ({
      fields,
      onRemove,
      removeItemButtonText
    }: ArrayItemProps) => {
      return (
        <Paper>
          {Object.keys(fields).map(field => (
            <Field key={field} {...fields[field]} />
          ))}
          <Button variant="text" onClick={onRemove}>
            {removeItemButtonText}
          </Button>
        </Paper>
      );
    },
    renderAddButton: ({ value, onClick }: AddButtonProps) => (
      <Button
        variant="outlined"
        iconLeft="add"
        onClick={onClick}
        style={{
          marginRight: 16,
          marginTop: 8,
          display: 'inline-flex',
          alignItems: 'center'
        }}
      >
        {value}
      </Button>
    )
  };

  handleResetItem = (id: number) => {
    const indexToReset = this.props.value.findIndex(item => item._id === id);
    const newItems = [...this.props.value];

    const newItem = {
      _id: id,
      ...getSchemaValues(evaluateSubschema(this.props.schema.items, {}), {})
    };

    newItems.splice(indexToReset, 1, newItem);

    this.props.onChange({
      target: {
        name: this.props.name,
        value: newItems
      }
    });

    this.props.onBlur({
      target: {
        name: this.props.name,
        touched: this.props.touched.filter(item => item._id !== id)
      }
    });
  };

  handleAddItem = (e: MouseEvent) => {
    e.preventDefault();

    if (this.props.resetNextClickCount) {
      this.props.resetNextClickCount();
    }

    const lastItem =
      this.props.value.length > 0
        ? this.props.value[this.props.value.length - 1]
        : null;

    const newItem = {
      _id: lastItem ? lastItem._id + 1 : guid(),
      ...getSchemaValues(evaluateSubschema(this.props.schema.items, {}), {})
    };

    this.props.onChange({
      target: {
        name: this.props.name,
        value: this.props.value.concat(newItem)
      }
    });
  };

  handleRemoveItem = (id: number) => {
    this.props.onChange({
      target: {
        name: this.props.name,
        value: this.props.value.filter(item => item._id !== id)
      }
    });

    this.props.onBlur({
      target: {
        name: this.props.name,
        touched: this.props.touched.filter(item => item._id !== id)
      }
    });
  };

  handleItemFieldChange = (id: number, e: FieldChangeEvent) => {
    this.props.onChange({
      target: {
        name: this.props.name,
        value: this.props.value.map(item => {
          if (item._id === id) {
            const updatedValue = {
              ...item,
              [e.target.name]: e.target.value
            };

            return getSchemaValues(
              evaluateSubschema(this.props.schema.items, updatedValue),
              updatedValue
            );
          }

          return item;
        })
      }
    });
  };

  handleItemFieldBlur = (itemId: number, e: FieldBlurEvent) => {
    const existing = findByItemId(this.props.touched, itemId, null);

    if (existing) {
      return this.props.onBlur({
        target: {
          name: this.props.name,
          touched: this.props.touched.map(item => {
            if (item._id === itemId) {
              const updatedTouched = {
                ...item,
                [e.target.name]: e.target.touched
              };

              return updatedTouched;
            }

            return item;
          })
        }
      });
    }

    return this.props.onBlur({
      target: {
        name: this.props.name,
        touched: this.props.touched.concat({
          _id: itemId,
          [e.target.name]: e.target.touched
        })
      }
    });
  };

  render() {
    const {
      renderItem,
      renderAddButton,
      addItemButtonText,
      schema,
      style,
      className,
      asTable,
      isFieldRequired,
      showOptionalHint,
      ...props
    } = this.props;

    if (!isObjectSchema(schema.items)) {
      throw new Error(
        `<ArrayField /> only accepts ObjectSchemas. "${JSON.stringify(
          schema
        )} was given"`
      );
    }

    const { items } = schema;

    const fieldsForArrayItem = (id: number) => {
      const value = findByItemId(props.value, id, {});
      const itemSchema = evaluateSubschema(items, value) as ObjectSchema;
      const item = itemSchema.properties || {};

      const subFieldKeys = orderProperties(
        Object.keys(item),
        (items.$uiOptions || {}).propertyOrder
      );

      const subFieldErrors: Errors<ObjectSchema> = findByItemId(
        props.errors.items as any,
        id,
        {}
      );

      const subFieldTouched: Touched<ObjectSchema> = findByItemId(
        props.touched,
        id,
        {}
      );

      return subFieldKeys.reduce<{ [key: string]: FieldProps<Schema> }>(
        (subFields, subKey) => {
          subFields[subKey] = {
            schema: item[subKey],
            id: `${props.id}__${subKey}__${id}`,
            name: `${props.name}__${subKey}__${id}`,
            formName: props.formName,
            onChange: e =>
              this.handleItemFieldChange(id, {
                target: {
                  name: subKey,
                  value: e.target.value
                }
              }),
            onBlur: e => {
              return this.handleItemFieldBlur(id, {
                target: {
                  name: subKey,
                  touched: e.target.touched
                }
              });
            },
            value: value[subKey],
            errors: subFieldErrors[subKey],
            touched: subFieldTouched[subKey],
            forceShowError: props.forceShowError
          };

          return subFields;
        },
        {}
      );
    };

    const showGeneralError = !!(
      this.props.forceShowError &&
      this.props.errors &&
      this.props.errors.all.length > 0
    );

    const Wrapper = asTable ? 'table' : 'div';

    const $uiOptions = schema.$uiOptions || {};
    const showHint =
      $uiOptions.hint !== undefined ||
      (showOptionalHint && isFieldRequired === false);

    return (
      <Wrapper
        role="group"
        aria-labelledby="legend"
        style={{ marginTop: 0, minWidth: 0, ...(style ?? {}) }}
        className={className}
      >
        {(schema.title || schema.description) && (
          <div
            style={{
              marginBottom: $uiOptions.hint ? 8 : 16
            }}
          >
            <div style={{ display: 'flex' }} />
            {schema.title && (
              <Legend id="legend" error={showGeneralError}>
                {schema.title}
              </Legend>
            )}

            {schema.description && (
              <Popover ml="8px" id={`${props.id}_description`}>
                {schema.description}
              </Popover>
            )}
          </div>
        )}

        {showHint && (
          <div
            id={`${props.id}_hint`}
            className="dsm-form-hint"
            style={{ marginBottom: 8 }}
          >
            {showOptionalHint ? (
              <OptionalHint hint={$uiOptions.hint} />
            ) : (
              $uiOptions.hint
            )}
          </div>
        )}

        {showGeneralError && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              fluid
              variant="error"
              title={joinErrors(this.props.errors.all, this.props.getText)}
            />
          </div>
        )}

        {props.value.map((item, index) => (
          <HeavilyOptimizedArrayItemField
            key={item._id}
            id={item._id}
            fields={fieldsForArrayItem(item._id)}
            render={renderItem!}
            index={index}
            onRemove={() => this.handleRemoveItem(item._id)}
            onReset={() => this.handleResetItem(item._id)}
            removeItemButtonText={this.props.removeItemButtonText}
            noOptimalisation={this.props.noOptimalisation}
            isLastItem={index === props.value.length - 1}
          />
        ))}

        {renderAddButton &&
          !this.props.readOnly &&
          maxItemsNotExceeded(
            this.props.value.length,
            this.props.schema.maxItems
          ) &&
          renderAddButton({
            value: addItemButtonText || 'Legg til flere',
            onClick: this.handleAddItem
          })}
      </Wrapper>
    );
  }
}

const maxItemsNotExceeded = (itemCount: number, maxItems?: number) => {
  if (maxItems === undefined) return true;
  return itemCount < maxItems;
};

export default ArrayField;
